/**
 * Dashboard API service - handles dashboard stats and activity
 */

import { apiClient } from './client';
import type { Task } from './tasks';
import type { Meeting } from './meetings';

// Types
export interface DashboardStats {
  totalTasks: number;
  completedTasks: number;
  inProgressTasks: number;
  overdueTasks: number;
  totalProjects: number;
  activeProjects: number;
  totalMeetings: number;
  upcomingMeetings: number;
  completionRate: number;
}

export interface ActivityItem {
  id: string;
  type: 'task_created' | 'task_updated' | 'task_completed' | 'task_moved' | 'comment_added' | 'meeting_created' | 'meeting_completed';
  description: string;
  entityId: string;
  entityType: 'task' | 'meeting' | 'project' | 'board';
  entityTitle: string;
  userId: string;
  userName: string;
  userAvatarUrl?: string | undefined;
  projectId?: string | undefined;
  projectName?: string | undefined;
  createdAt: string;
}

export interface TaskDueSoon extends Pick<Task, 'id' | 'title' | 'priority' | 'dueDate'> {
  boardId: string;
  boardName: string;
  projectName: string;
  projectId: string;
  daysUntilDue: number;
}

export interface MyTask {
  id: string;
  title: string;
  description?: string | undefined;
  columnId: string;
  priority: string;
  dueDate?: string | undefined;
  storyPoints?: number | undefined;
  assigneeId?: string | undefined;
  boardId?: string | undefined;
  boardName?: string | undefined;
  projectId?: string | undefined;
  projectName?: string | undefined;
  taskKey?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
}

export interface ProjectSummary {
  id: string;
  name: string;
  key: string;
  color?: string | undefined;
  taskCount: number;
  completedTaskCount: number;
  completionRate: number;
  recentActivity: Date;
}

export interface DashboardData {
  stats: DashboardStats;
  recentActivity: ActivityItem[];
  tasksDueSoon: TaskDueSoon[];
  myTasks: MyTask[];
  projects: ProjectSummary[];
  upcomingMeetings: Meeting[];
}

export interface FocusTask {
  taskId: string;
  boardId: string;
  title: string;
  priority: string;
  dueDate?: string | undefined;
  projectName?: string | undefined;
  reason: string;
  urgencyScore: number;
  importanceScore: number;
  focusScore: number;
}

export interface MeetingPrepItem {
  meetingId: string;
  title: string;
  startTime?: string | undefined;
  relatedTasks: string[];
  prepNotes?: string | undefined;
}

export interface BlockedTaskItem {
  taskId: string;
  title: string;
  blockedBy: string;
}

export interface TodaysFocusData {
  recommendedTasks: FocusTask[];
  summary: string;
  meetingContext?: string | undefined;
  productivityTip?: string | undefined;
  estimatedCompletionTime?: string | undefined;
  meetingPrep: MeetingPrepItem[];
  blockedTasks: BlockedTaskItem[];
}

/**
 * Dashboard API methods
 */
export const dashboardApi = {
  /**
   * Get full dashboard data
   */
  async getDashboard(organizationId: string): Promise<DashboardData> {
    const response = await apiClient.get<{
      stats: {
        total_tasks: number;
        completed_tasks: number;
        in_progress_tasks: number;
        overdue_tasks: number;
        total_projects: number;
        active_projects: number;
        total_meetings: number;
        upcoming_meetings: number;
        completion_rate: number;
      };
      recent_activity: Array<{
        id: string;
        type: string;
        description: string;
        entity_id: string;
        entity_type: string;
        entity_title: string;
        user_id: string;
        user_name: string;
        user_avatar_url?: string;
        project_id?: string;
        project_name?: string;
        created_at: string;
      }>;
      tasks_due_soon: Array<{
        id: string;
        title: string;
        priority: string;
        due_date: string;
        board_id: string;
        board_name: string;
        project_name: string;
        project_id: string;
        days_until_due: number;
      }>;
      my_tasks: Array<{
        id: string;
        title: string;
        description?: string;
        column_id: string;
        priority: string;
        due_date?: string;
        story_points?: number;
        assignee_id?: string;
        board_id?: string;
        board_name?: string;
        project_id?: string;
        project_name?: string;
        task_key?: string;
        created_at?: string;
        updated_at?: string;
      }>;
      projects: Array<{
        id: string;
        name: string;
        key: string;
        color?: string;
        task_count: number;
        completed_task_count: number;
        completion_rate: number;
        recent_activity: string;
      }>;
      upcoming_meetings: unknown[];
    }>('/dashboard', {
      params: { organization_id: organizationId },
    });

    const data = response.data as any;

    return {
      stats: {
        totalTasks: data.stats.totalTasks ?? data.stats.total_tasks,
        completedTasks: data.stats.completedTasks ?? data.stats.completed_tasks,
        inProgressTasks: data.stats.inProgressTasks ?? data.stats.in_progress_tasks,
        overdueTasks: data.stats.overdueTasks ?? data.stats.overdue_tasks,
        totalProjects: data.stats.totalProjects ?? data.stats.total_projects,
        activeProjects: data.stats.activeProjects ?? data.stats.active_projects,
        totalMeetings: data.stats.totalMeetings ?? data.stats.total_meetings,
        upcomingMeetings: data.stats.upcomingMeetings ?? data.stats.upcoming_meetings,
        completionRate: data.stats.completionRate ?? data.stats.completion_rate,
      },
      recentActivity: (data.recentActivity ?? data.recent_activity ?? []).map((item: any) => ({
        id: item.id,
        type: item.type as ActivityItem['type'],
        description: item.description,
        entityId: item.entityId ?? item.entity_id,
        entityType: (item.entityType ?? item.entity_type) as ActivityItem['entityType'],
        entityTitle: item.entityTitle ?? item.entity_title,
        userId: item.userId ?? item.user_id,
        userName: item.userName ?? item.user_name,
        userAvatarUrl: item.userAvatarUrl ?? item.user_avatar_url,
        projectId: item.projectId ?? item.project_id,
        projectName: item.projectName ?? item.project_name,
        createdAt: item.createdAt ?? item.created_at,
      })),
      tasksDueSoon: (data.tasksDueSoon ?? data.tasks_due_soon ?? []).map((task: any) => ({
        id: task.id,
        title: task.title,
        priority: task.priority as Task['priority'],
        dueDate: task.dueDate ?? task.due_date,
        boardId: task.boardId ?? task.board_id,
        boardName: task.boardName ?? task.board_name,
        projectName: task.projectName ?? task.project_name,
        projectId: task.projectId ?? task.project_id,
        daysUntilDue: task.daysUntilDue ?? task.days_until_due,
      })),
      myTasks: (data.myTasks ?? data.my_tasks ?? []).map((task: any) => ({
        id: task.id,
        title: task.title,
        description: task.description,
        columnId: task.columnId ?? task.column_id,
        priority: task.priority,
        dueDate: task.dueDate ?? task.due_date,
        storyPoints: task.storyPoints ?? task.story_points,
        assigneeId: task.assigneeId ?? task.assignee_id,
        boardId: task.boardId ?? task.board_id,
        boardName: task.boardName ?? task.board_name,
        projectId: task.projectId ?? task.project_id,
        projectName: task.projectName ?? task.project_name,
        taskKey: task.taskKey ?? task.task_key,
        createdAt: task.createdAt ?? task.created_at,
        updatedAt: task.updatedAt ?? task.updated_at,
      })),
      projects: (data.projects ?? []).map((project: any) => ({
        id: project.id,
        name: project.name,
        key: project.key,
        color: project.color,
        taskCount: project.taskCount ?? project.task_count,
        completedTaskCount: project.completedTaskCount ?? project.completed_task_count,
        completionRate: project.completionRate ?? project.completion_rate,
        recentActivity: new Date(project.recentActivity ?? project.recent_activity),
      })),
      upcomingMeetings: (data.upcomingMeetings ?? data.upcoming_meetings ?? []) as Meeting[],
    };
  },

  /**
   * Get dashboard stats only
   */
  async getStats(organizationId: string): Promise<DashboardStats> {
    const response = await apiClient.get<any>('/dashboard/stats', {
      params: { organization_id: organizationId },
    });

    const data = response.data;
    return {
      totalTasks: data.totalTasks ?? data.total_tasks,
      completedTasks: data.completedTasks ?? data.completed_tasks,
      inProgressTasks: data.inProgressTasks ?? data.in_progress_tasks,
      overdueTasks: data.overdueTasks ?? data.overdue_tasks,
      totalProjects: data.totalProjects ?? data.total_projects,
      activeProjects: data.activeProjects ?? data.active_projects,
      totalMeetings: data.totalMeetings ?? data.total_meetings,
      upcomingMeetings: data.upcomingMeetings ?? data.upcoming_meetings,
      completionRate: data.completionRate ?? data.completion_rate,
    };
  },

  /**
   * Get recent activity
   */
  async getActivity(
    organizationId: string,
    limit = 20
  ): Promise<ActivityItem[]> {
    const response = await apiClient.get<any[]>('/dashboard/activity', {
      params: { organization_id: organizationId, limit },
    });

    return response.data.map((item) => ({
      id: item.id,
      type: item.type as ActivityItem['type'],
      description: item.description,
      entityId: item.entityId ?? item.entity_id,
      entityType: (item.entityType ?? item.entity_type) as ActivityItem['entityType'],
      entityTitle: item.entityTitle ?? item.entity_title,
      userId: item.userId ?? item.user_id,
      userName: item.userName ?? item.user_name,
      userAvatarUrl: item.userAvatarUrl ?? item.user_avatar_url,
      projectId: item.projectId ?? item.project_id,
      projectName: item.projectName ?? item.project_name,
      createdAt: item.createdAt ?? item.created_at,
    }));
  },

  /**
   * Get my assigned tasks
   */
  async getMyTasks(organizationId: string, limit = 10): Promise<Task[]> {
    const response = await apiClient.get<unknown[]>('/dashboard/my-tasks', {
      params: { organization_id: organizationId, limit },
    });
    return response.data as Task[];
  },

  /**
   * Get AI-powered today's focus recommendations
   */
  async getTodaysFocus(
    organizationId: string,
    maxTasks = 5
  ): Promise<TodaysFocusData> {
    const response = await apiClient.get<any>('/dashboard/todays-focus', {
      params: { organization_id: organizationId, max_tasks: maxTasks },
    });

    const data = response.data;
    const recommendedTasks = data.recommendedTasks ?? data.recommended_tasks ?? [];
    return {
      recommendedTasks: recommendedTasks.map((task: any) => ({
        taskId: task.taskId ?? task.task_id,
        boardId: task.boardId ?? task.board_id,
        title: task.title,
        priority: task.priority,
        dueDate: task.dueDate ?? task.due_date,
        projectName: task.projectName ?? task.project_name,
        reason: task.reason,
        urgencyScore: task.urgencyScore ?? task.urgency_score,
        importanceScore: task.importanceScore ?? task.importance_score,
        focusScore: task.focusScore ?? task.focus_score,
      })),
      summary: data.summary,
      meetingContext: data.meetingContext ?? data.meeting_context,
      productivityTip: data.productivityTip ?? data.productivity_tip,
      estimatedCompletionTime: data.estimatedCompletionTime ?? data.estimated_completion_time,
      meetingPrep: (data.meetingPrep ?? data.meeting_prep ?? []).map((item: any) => ({
        meetingId: item.meetingId ?? item.meeting_id,
        title: item.title,
        startTime: item.startTime ?? item.start_time,
        relatedTasks: item.relatedTasks ?? item.related_tasks ?? [],
        prepNotes: item.prepNotes ?? item.prep_notes,
      })),
      blockedTasks: (data.blockedTasks ?? data.blocked_tasks ?? []).map((item: any) => ({
        taskId: item.taskId ?? item.task_id,
        title: item.title,
        blockedBy: item.blockedBy ?? item.blocked_by,
      })),
    };
  },

  /**
   * Manually trigger daily briefing for the current user
   */
  async sendTodaysFocusNow(organizationId: string): Promise<{ status: string; message: string }> {
    const response = await apiClient.post<{ status: string; message: string }>(
      '/dashboard/todays-focus/send-now',
      null,
      { params: { organization_id: organizationId } },
    );
    return response.data;
  },
};

export default dashboardApi;
